<script setup>
import { onMounted, ref, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

import SettingsLayout from '../SettingsLayout.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import StageAutomationDialog from 'dashboard/components-next/Crm/StageAutomationDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const route = useRoute();
const router = useRouter();
const store = usePipelinesStore();
const { t } = useI18n();

const pipeline = ref(null);
const tab = ref('general');
const saving = ref(false);

const form = reactive({
  name: '',
  description: '',
  color: '#4f46e5',
  active: true,
  value_enabled: true,
  assignment_strategy: 'inherit_conversation',
});

const stages = ref([]);
const lostReasons = ref([]);
const newStage = reactive({ name: '', kind: 'open', color: '#94a3b8' });
const newReason = reactive({ name: '' });

const automationDialogRef = ref(null);
const automationStage = ref(null);

const removeStageDialogRef = ref(null);
const stageToRemove = ref(null);

function openAutomations(stage) {
  automationStage.value = stage;
  automationDialogRef.value?.open();
}

async function saveAutomations(hooks) {
  if (!automationStage.value) return;
  try {
    const updated = await store.updateStage(
      pipeline.value.id,
      automationStage.value.id,
      {
        stage: { automation_hooks: hooks },
      }
    );
    // Atualiza apenas o stage afetado na lista local
    stages.value = stages.value.map(s => (s.id === updated.id ? updated : s));
    useAlert('Automações salvas.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar automações.');
  }
}

const tabs = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-settings' },
  { key: 'stages', label: 'Estágios', icon: 'i-lucide-layers' },
  { key: 'lost_reasons', label: 'Motivos de perda', icon: 'i-lucide-x-circle' },
  { key: 'qualification', label: 'Qualificação', icon: 'i-lucide-list-checks' },
  { key: 'assignment', label: 'Atribuição', icon: 'i-lucide-user-check' },
];

const stageKinds = [
  { value: 'open', label: 'Aberto' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

// Rótulo do tipo de estágio em pt-BR (badge mostrava o valor cru: OPEN/WON/LOST).
const kindLabel = kind =>
  stageKinds.find(k => k.value === kind)?.label || kind;

const assignmentStrategies = [
  { value: 'inherit_conversation', label: 'Herdar da conversa' },
  { value: 'round_robin', label: 'Round-robin' },
  { value: 'least_loaded', label: 'Menos carregado' },
  { value: 'manual', label: 'Manual' },
];

const hasDefaultWon = computed(() =>
  stages.value.some(s => s.kind === 'won' && s.default_for_kind)
);

const breadcrumbItems = computed(() => [
  { label: t('PIPELINES.HEADER'), routeName: 'settings_pipelines' },
  { label: pipeline.value?.name || 'Editar pipeline' },
]);

const rawCriteriaJson = computed({
  get() {
    try {
      return JSON.stringify(
        pipeline.value?.qualification_criteria || {},
        null,
        2
      );
    } catch {
      return '{}';
    }
  },
  set(v) {
    try {
      pipeline.value.qualification_criteria = JSON.parse(v);
    } catch {
      /* ignora json inválido durante digitação */
    }
  },
});

async function load() {
  pipeline.value = await store.fetchOne(route.params.pipelineId);
  Object.assign(form, {
    name: pipeline.value.name,
    description: pipeline.value.description || '',
    color: pipeline.value.color,
    active: pipeline.value.active,
    value_enabled: pipeline.value.value_enabled,
    assignment_strategy: pipeline.value.assignment_strategy,
  });
  stages.value = [...pipeline.value.stages];
  lostReasons.value = pipeline.value.lost_reasons || [];
}
onMounted(load);

function handleBreadcrumbClick(item) {
  if (item.routeName) router.push({ name: item.routeName });
}

async function save() {
  saving.value = true;
  try {
    await store.update(pipeline.value.id, form);
    useAlert('Pipeline salvo.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar.');
  } finally {
    saving.value = false;
  }
}

async function saveCriteria() {
  saving.value = true;
  try {
    await store.update(pipeline.value.id, {
      qualification_criteria: pipeline.value.qualification_criteria,
    });
    useAlert('Critérios salvos.');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao salvar critérios.');
  } finally {
    saving.value = false;
  }
}

async function addStage() {
  if (!newStage.name.trim()) return;
  try {
    const created = await store.createStage(pipeline.value.id, {
      name: newStage.name,
      kind: newStage.kind,
      color: newStage.color,
      position: stages.value.length,
    });
    stages.value.push(created);
    newStage.name = '';
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao adicionar stage.');
  }
}

function removeStage(stage) {
  stageToRemove.value = stage;
  removeStageDialogRef.value?.open();
}

async function confirmRemoveStage() {
  const stage = stageToRemove.value;
  if (!stage) return;
  try {
    await store.destroyStage(pipeline.value.id, stage.id);
    stages.value = stages.value.filter(s => s.id !== stage.id);
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao remover.');
  } finally {
    removeStageDialogRef.value?.close();
    stageToRemove.value = null;
  }
}

async function addLostReason() {
  if (!newReason.name.trim()) return;
  const created = await store.createLostReason(pipeline.value.id, {
    name: newReason.name,
    position: lostReasons.value.length,
  });
  lostReasons.value.push(created);
  newReason.name = '';
}
</script>

<template>
  <SettingsLayout
    class="w-full max-w-3xl ltr:mr-auto rtl:ml-auto"
    :is-loading="!pipeline"
  >
    <template #header>
      <div class="flex items-center gap-2 w-full justify-between mb-4 min-h-10">
        <Breadcrumb :items="breadcrumbItems" @click="handleBreadcrumbClick" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-10">
        <!-- Header do pipeline -->
        <div class="flex items-center gap-3">
          <span
            class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
            :style="{ background: `${pipeline?.color}1f` }"
          >
            <i
              class="i-lucide-kanban size-5"
              :style="{ color: pipeline?.color }"
            />
          </span>
          <div class="min-w-0">
            <h1 class="text-heading-3 text-n-slate-12 truncate">
              {{ pipeline?.name }}
            </h1>
            <p
              v-if="pipeline?.description"
              class="text-body-small text-n-slate-11 truncate"
            >
              {{ pipeline?.description }}
            </p>
          </div>
        </div>

        <!-- Tabs -->
        <nav class="flex gap-1 border-b border-n-weak">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-body-main border-b-2 transition"
            :class="
              tab === t.key
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="tab = t.key"
          >
            <i :class="t.icon" class="size-3.5" />
            {{ t.label }}
          </button>
        </nav>

        <!-- Geral -->
        <div v-if="tab === 'general'" class="flex flex-col gap-4 max-w-2xl">
          <div class="flex items-center gap-6">
            <WithLabel
              label="Nome"
              name="name"
              class="w-full"
            >
              <div class="flex-1">
                <Input
                  v-model="form.name"
                  type="text"
                  placeholder="Ex: Vendas Premium"
                />
              </div>
            </WithLabel>
          </div>

          <div class="flex items-center gap-6">
            <WithLabel
              label="Descrição"
              name="description"
              class="w-full"
            >
              <div class="flex-1">
                <Input
                  v-model="form.description"
                  type="text"
                  placeholder="Descreva brevemente este pipeline"
                />
              </div>
            </WithLabel>
          </div>

          <div class="flex items-center gap-6">
            <WithLabel
              label="Cor"
              name="color"
              class="w-full"
            >
              <div class="flex-1">
                <woot-color-picker v-model="form.color" />
              </div>
            </WithLabel>
          </div>

          <hr class="border-n-weak" />

          <label class="flex items-start gap-3 cursor-pointer">
            <Checkbox v-model="form.active" class="mt-1" />
            <div>
              <div class="text-body-main font-medium text-n-slate-12">
                Ativo
              </div>
              <p class="text-body-small text-n-slate-11">
                Pipelines inativos somem do kanban e dos selects de criação.
              </p>
            </div>
          </label>

          <label class="flex items-start gap-3 cursor-pointer">
            <Checkbox v-model="form.value_enabled" class="mt-1" />
            <div>
              <div class="text-body-main font-medium text-n-slate-12">
                Habilitar campo "Valor" nos leads
              </div>
              <p class="text-body-small text-n-slate-11">
                Mostra valor em cards e exige no fechamento de ganhos.
              </p>
            </div>
          </label>

          <div class="pt-2">
            <NextButton
              :is-loading="saving"
              label="Salvar alterações"
              @click="save"
            />
          </div>
        </div>

        <!-- Stages -->
        <div v-if="tab === 'stages'" class="flex flex-col gap-4">
          <div
            v-if="!hasDefaultWon"
            class="flex items-start gap-3 p-3 rounded-xl bg-n-amber-3 border border-n-amber-7"
          >
            <i class="i-lucide-alert-triangle size-4 text-n-amber-11 mt-0.5" />
            <p class="text-body-small text-n-amber-12 mb-0">
              Este pipeline não tem stage <b>"Ganho" padrão</b>. Marque um stage
              de tipo "Ganho" como default para que a IA e ações de fechamento
              funcionem.
            </p>
          </div>

          <ol class="flex flex-col gap-2">
            <li
              v-for="(s, i) in stages"
              :key="s.id"
              class="rounded-xl border border-n-weak px-4 py-3 flex items-center gap-3"
            >
              <span
                class="text-body-small text-n-slate-10 w-6 text-center font-mono"
                >{{ i }}</span
              >
              <span
                class="size-3 rounded-full"
                :style="{ background: s.color }"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-body-main font-medium text-n-slate-12">{{
                    s.name
                  }}</span>
                  <span
                    class="text-label-small px-1.5 py-0.5 rounded font-medium"
                    :class="
                      s.kind === 'won'
                        ? 'bg-n-grass-3 text-n-grass-11'
                        : s.kind === 'lost'
                          ? 'bg-n-ruby-3 text-n-ruby-11'
                          : 'bg-n-blue-3 text-n-blue-11'
                    "
                    >{{ kindLabel(s.kind) }}</span
                  >
                  <span
                    v-if="s.default_for_kind"
                    class="text-label-small px-1.5 py-0.5 rounded bg-n-brand/10 text-n-brand font-medium"
                    >Padrão</span
                  >
                </div>
                <!-- Linha de configuração do estágio (preenche o espaço com info útil) -->
                <div
                  class="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-label-small text-n-slate-10"
                >
                  <span class="inline-flex items-center gap-1">
                    <i class="i-lucide-hash size-3" />
                    Posição {{ i + 1 }} de {{ stages.length }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <i class="i-lucide-clock size-3" />
                    <template v-if="s.stale_after_days">
                      Parado após {{ s.stale_after_days }} dia(s)
                    </template>
                    <template v-else>Sem alerta de parado</template>
                  </span>
                  <span
                    class="inline-flex items-center gap-1"
                    :class="
                      (s.automation_hooks || []).length ? 'text-n-brand' : ''
                    "
                  >
                    <i class="i-lucide-zap size-3" />
                    {{ (s.automation_hooks || []).length }} automação(ões)
                  </span>
                </div>
              </div>
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-zap"
                v-tooltip="'Configurar automações ao entrar neste stage'"
                @click="openAutomations(s)"
              />
              <NextButton
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                @click="removeStage(s)"
              />
            </li>
          </ol>

          <!-- Adicionar stage - todos os campos na MESMA LINHA, alinhados pela baseline -->
          <div class="rounded-xl border border-dashed border-n-weak p-4 mt-2">
            <h3 class="text-heading-3 text-n-slate-12 mb-3">Adicionar estágio</h3>
            <div class="grid grid-cols-[1fr,11rem,4rem,auto] gap-3 items-end">
              <div>
                <label class="text-body-small text-n-slate-11 block mb-1"
                  >Nome</label
                >
                <Input v-model="newStage.name" placeholder="Ex: Proposta" />
              </div>
              <div>
                <label class="text-body-small text-n-slate-11 block mb-1"
                  >Tipo</label
                >
                <SelectInput
                  v-model="newStage.kind"
                  :options="stageKinds"
                  class="w-full [&>select]:w-full"
                />
              </div>
              <div>
                <label class="text-body-small text-n-slate-11 block mb-1"
                  >Cor</label
                >
                <woot-color-picker v-model="newStage.color" />
              </div>
              <NextButton
                label="Adicionar"
                icon="i-lucide-plus"
                @click="addStage"
              />
            </div>
          </div>
        </div>

        <!-- Lost reasons -->
        <div
          v-if="tab === 'lost_reasons'"
          class="flex flex-col gap-4 max-w-2xl"
        >
          <ol v-if="lostReasons.length" class="flex flex-col gap-2">
            <li
              v-for="r in lostReasons"
              :key="r.id"
              class="rounded-xl border border-n-weak px-4 py-3 flex items-center justify-between"
            >
              <span class="text-body-main text-n-slate-12">{{ r.name }}</span>
              <span
                class="text-label-small px-1.5 py-0.5 rounded uppercase font-medium"
                :class="
                  r.active
                    ? 'bg-n-grass-3 text-n-grass-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
                >{{ r.active ? 'ativo' : 'inativo' }}</span
              >
            </li>
          </ol>
          <p v-else class="text-body-main text-n-slate-11">
            Nenhum motivo de perda cadastrado.
          </p>

          <div class="rounded-xl border border-dashed border-n-weak p-4">
            <h3
              class="text-label-small font-semibold text-n-slate-12 uppercase tracking-wide mb-3"
            >
              Adicionar motivo
            </h3>
            <div class="flex gap-2 items-end">
              <div class="flex-1">
                <Input
                  v-model="newReason.name"
                  label="Nome"
                  placeholder="Ex: Preço, Concorrência..."
                />
              </div>
              <NextButton
                label="Adicionar"
                icon="i-lucide-plus"
                @click="addLostReason"
              />
            </div>
          </div>
        </div>

        <!-- Qualification -->
        <div
          v-if="tab === 'qualification'"
          class="flex flex-col gap-3 max-w-2xl"
        >
          <p class="text-body-main text-n-slate-11">
            Critérios avaliados quando um lead é qualificado. UI gráfica chega
            em release seguinte; por ora, edite o JSON diretamente.
          </p>
          <textarea
            v-model="rawCriteriaJson"
            rows="14"
            class="w-full font-mono text-body-small rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2"
            placeholder='{ "items": [...], "min_required": 2 }'
          />
          <div>
            <NextButton
              :is-loading="saving"
              label="Salvar critérios"
              @click="saveCriteria"
            />
          </div>
        </div>

        <!-- Assignment -->
        <div v-if="tab === 'assignment'" class="flex flex-col gap-4 max-w-2xl">
          <WithLabel
            label="Estratégia"
            name="assignment_strategy"
            class="w-full"
          >
            <div class="flex-1">
              <SelectInput
                v-model="form.assignment_strategy"
                :options="assignmentStrategies"
                class="w-full [&>select]:w-full"
              />
            </div>
          </WithLabel>
          <div>
            <NextButton
              :is-loading="saving"
              label="Salvar alterações"
              @click="save"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- Dialog de automações por stage (Fase 1.5) -->
    <StageAutomationDialog
      ref="automationDialogRef"
      :stage="automationStage"
      @save="saveAutomations"
    />

    <Dialog
      ref="removeStageDialogRef"
      type="alert"
      title="Remover stage"
      :description="`Remover o stage “${stageToRemove?.name}”? Esta ação não pode ser desfeita.`"
      confirm-button-label="Remover"
      cancel-button-label="Cancelar"
      @confirm="confirmRemoveStage"
    />
  </SettingsLayout>
</template>
